import { Component, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { currentRedactionActions } from '@store/doc-viewer/redaction-editor/redaction-editor.actions';
import { currentRedactionEditor, currentActiveRedactions, isManualRedaction, hasPdfAndNativeRedaction } from '@store/doc-viewer/redaction-editor/redaction-editor.selectors';
import { RedactionEditorState } from '@store/doc-viewer/redaction-editor/redaction-editor.state';
import { ComparisonUtility } from '@utilities/comparison.utility';
import { cloneDeep } from 'lodash-es';
import { Observable, distinctUntilChanged, map, takeUntil } from 'rxjs';
import { BaseComponent } from 'src/app/components/base.component';
import { HighlightColor } from 'src/app/components/review/redaction-annotation/highlight-color.model';
import { LifeCycleEdit } from 'src/app/components/review/redaction-annotation/life-cycle-edit.enum';
import { RedactionAnnotationEdit } from 'src/app/components/review/redaction-annotation/redaction-annotation-edit.model';

@Component({
    selector: 'lex-redaction-list',
    templateUrl: './redaction-list.component.html',
    styleUrls: ['./redaction-list.component.scss'],
    standalone: false
})
export class RedactionListComponent extends BaseComponent implements OnInit {
  public showManualMessage: boolean = false;
  public showNativeRedactionMessage: boolean = false;
  public redactions: RedactionAnnotationEdit[] = [];
  public currentRedactionId: number;
  public pageRedactionMap: Array<{ pageNum: number, redactions: RedactionAnnotationEdit[] }> = new Array<{ pageNum: number, redactions: RedactionAnnotationEdit[] }>();
  public editItemTextId: number = null;
  public editItemColorId: number = null;
  public redactionColors: HighlightColor[] = [];
  public redactionDisplayNames: Map<string, string> = new Map([
    ['69b9cfdb-6f1a-4631-bb51-d1d5a30ce1aa', 'Black'],
    ['a2d86281-46c8-4a12-9cf5-f665c5e89e57', 'White']
  ]);
  public displayColumns = ['text', 'color', 'delete'];

  private redactionEditor$: Observable<RedactionEditorState> = this.store.select(currentRedactionEditor());
  private redactions$: Observable<RedactionAnnotationEdit[]> = this.store.select(currentActiveRedactions);
  private manualRedaction$: Observable<boolean> = this.store.select(isManualRedaction);
  private nativeRedaction$ = this.store.select(hasPdfAndNativeRedaction);
  private tempRedactionText: string = null;
  constructor(private store: Store) {
    super();
  }

  ngOnInit(): void {
    this.redactionEditor$.pipe(
      map((editor) => {
        if (this.currentRedactionId !== editor.currentRedactionId)
          this.currentRedactionId = editor.currentRedactionId;
        this.redactionColors = editor.colors;
      }),
      takeUntil(this.destroy$)
    ).subscribe();

    this.redactions$.pipe(
      distinctUntilChanged((a, b) => ComparisonUtility.isEqual(a, b)),
      map((redactions) => {
        this.updatePageRedactionMap(redactions);
      }),
      takeUntil(this.destroy$)
    ).subscribe();

    this.manualRedaction$.pipe(
			takeUntil(this.destroy$)
		).subscribe((hasPdf) => this.showManualMessage = hasPdf);

    this.nativeRedaction$.pipe(
			takeUntil(this.destroy$)
		).subscribe((hasRedaction) => this.showNativeRedactionMessage = hasRedaction);
  }

  public getActiveRedactionCount(redactions: RedactionAnnotationEdit[]): number {
    return redactions.filter(e => e.status != LifeCycleEdit.Deleted).length;
  }

  public setSelectedRedaction(id: number): void {
    setTimeout(() => this.store.dispatch(currentRedactionActions.setCurrentRedaction({ redactionId: id })));
  }

  public updateRedaction(edit: RedactionAnnotationEdit): void {
    this.store.dispatch(currentRedactionActions.updateRedaction({ redaction: edit }));
    this.editItemTextId = null;
  }

  public deleteRedaction(redaction: RedactionAnnotationEdit): void {
    this.store.dispatch(currentRedactionActions.deleteRedaction({ redactionId: redaction.clientId }));
  }

  public setRedactionEditTextId(redaction: RedactionAnnotationEdit): void {
    this.editItemTextId = redaction.clientId;
    this.tempRedactionText = redaction.text;
  }

  public setRedactionText(redaction: RedactionAnnotationEdit): void {
    this.store.dispatch(currentRedactionActions.setRedactionText({ text: redaction.text }));
    this.updateRedaction(redaction);
  }

  public cancelTextEdit(redaction: RedactionAnnotationEdit): void {
    this.editItemTextId = null;
    redaction.text = this.tempRedactionText;
  }

  public getColor(color: HighlightColor): string {
    return this.redactionDisplayNames.get(color.highlightColorId);
  }

  public setColor(color: HighlightColor, edit: RedactionAnnotationEdit) {
    edit.highlightColor = color.colorValue;
    edit.highlightColorId = color.highlightColorId;
    this.store.dispatch(currentRedactionActions.setColorId({ colorId: color.highlightColorId }));
    this.updateRedaction(edit);
  }

  public getBackGroundColor(colorValue: string): string {
    return `#${colorValue}`;
  }

  public trackById(index: number, value: RedactionAnnotationEdit): number {
    return value.clientId;
  }

  public trackByPageNumber(index: number, value: any): number {
    return value.pageNum;
  }

  private updatePageRedactionMap(redactions: RedactionAnnotationEdit[]): void {
    const localRedactions = cloneDeep(redactions);
    const targetRedactions = localRedactions?.sort((a: RedactionAnnotationEdit, b: RedactionAnnotationEdit) => {
      if (a.y === b.y)
        if (a.x === b.x)
          return a.clientId > b.clientId ? 1 : -1;
        else
          return a.x > b.x ? 1 : -1;
      return a.y > b.y ? 1 : -1;
    });
    this.pageRedactionMap = [];
    targetRedactions?.forEach(r => {
      if (this.pageRedactionMap.findIndex(e => e.pageNum == r.rank) != -1)
        this.pageRedactionMap.find(e => e.pageNum == r.rank).redactions.push(r);
      else
        this.pageRedactionMap.push({
          pageNum: r.rank,
          redactions: [r]
        });
    });
    this.pageRedactionMap?.sort((a, b) => a.pageNum > b.pageNum ? 1 : -1);
  }
}
